import {PushNotificationsService} from './openapi/services/PushNotificationsService';

class PushNotificationService {
  private static instance: PushNotificationService;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private isInitialized: boolean = false;

  private constructor() {}

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      this.swRegistration = await navigator.serviceWorker.ready;
      this.isInitialized = true;
    } catch (error) {
      console.error('Service Worker initialization failed:', error);
      throw new Error(
        'Failed to initialize service worker: ' +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  public isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  public async getNotificationPermission(): Promise<NotificationPermission> {
    return Notification.permission;
  }

  public async requestNotificationPermission(): Promise<boolean> {
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw new Error(
        'Failed to request notification permission: ' +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  public async isSubscribed(): Promise<boolean> {
    if (!this.swRegistration) {
      await this.initialize();
    }

    if (!this.swRegistration) {
      throw new Error('Service Worker not available');
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      throw new Error(
        'Failed to check subscription status: ' +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  public async subscribeToPushNotifications(): Promise<void> {
    if (!this.swRegistration) {
      await this.initialize();
    }

    if (!this.swRegistration) {
      throw new Error('Service Worker not available');
    }

    // Check if already subscribed
    const existingSubscription = await this.swRegistration.pushManager.getSubscription();
    if (existingSubscription) {
      return;
    }

    try {
      // Get the VAPID public key from the server
      const response = await PushNotificationsService.getPublicKeyPushNotificationsPublicKeyGet();
      const publicKey = response.public_key || response;

      // Convert the VAPID key to a Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(publicKey);

      // Subscribe to push notifications
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey,
      });

      // Prepare the subscription data
      const subscriptionData = {
        endpoint: subscription.endpoint,
        p256dh_key: btoa(
          String.fromCharCode.apply(
            null,
            Array.from(new Uint8Array(subscription.getKey('p256dh') as ArrayBuffer))
          )
        ),
        auth_token: btoa(
          String.fromCharCode.apply(
            null,
            Array.from(new Uint8Array(subscription.getKey('auth') as ArrayBuffer))
          )
        ),
      };

      // Send the subscription to the server
      try {
        await PushNotificationsService.subscribePushNotificationsSubscribePost(subscriptionData);
      } catch (serverError) {
        console.error('Server error during subscription:', serverError);
        // If server subscription fails, unsubscribe from the browser
        await subscription.unsubscribe();
        throw new Error('Failed to register subscription with server. Please try again later.');
      }
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  public async unsubscribeFromPushNotifications(): Promise<void> {
    if (!this.swRegistration) {
      await this.initialize();
    }

    if (!this.swRegistration) {
      throw new Error('Service Worker not available');
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        // Unsubscribe from the browser
        await subscription.unsubscribe();

        // Notify the server
        try {
          await PushNotificationsService.unsubscribePushNotificationsUnsubscribeDelete(
            subscription.endpoint
          );
        } catch (serverError) {
          // Don't fail if server request fails, as we've already unsubscribed locally
          console.warn('Failed to notify server of unsubscription:', serverError);
        }
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  }

  public async testNotification(): Promise<void> {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    const permission = await this.getNotificationPermission();
    if (permission !== 'granted') {
      throw new Error(
        'Notification permission not granted. Please enable notifications in your browser settings.'
      );
    }

    try {
      const notification = new Notification('Test Notification', {
        body: 'Push notifications are working!',
        icon: '/logo.svg',
        tag: 'test-notification',
        requireInteraction: true,
        silent: false,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Error creating test notification:', error);
      throw new Error(
        'Failed to create test notification: ' +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  public async testServerNotification(): Promise<void> {
    try {
      // Get the current user ID from your auth system
      const userId = 'c58d1711-bfa4-4729-a903-13ed9e397b9d'; // Replace with actual user ID from your auth system

      // Send test notification request to server
      await PushNotificationsService.sendNotificationPushNotificationsSendPost(userId, {
        title: 'Test Server Notification',
        body: 'This is a test notification sent from the server!',
        icon: '/logo.svg',
        data: {
          timestamp: new Date().toISOString(),
          type: 'test',
        },
      });
    } catch (error) {
      console.error('Error sending test notification request:', error);
      throw new Error(
        'Failed to send test notification: ' +
          (error instanceof Error ? error.message : String(error))
      );
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    // 🔧 fix padding
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')  // 🔁 replace URL-safe chars
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

 /* private urlBase64ToUint8Array(base64String: string): Uint8Array {
    if (!base64String) {
      throw new Error('VAPID key is empty');
    }

    try {
      // Remove any whitespace
      base64String = base64String.trim();

      // Add padding if needed for base64 decoding
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const paddedBase64 = base64String + padding;

      // Decode the base64 string
      const binaryString = window.atob(paddedBase64);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Check if this looks like a raw key (65 bytes) or DER-encoded key (longer)
      if (bytes.length === 65) {
        // This is already a raw key
        return bytes;
      } else if (bytes.length > 65) {
        // This might be DER-encoded, try to extract the raw key
        // Look for the sequence that starts with 0x04 (uncompressed point indicator)
        let startIndex = -1;
        for (let i = 0; i < bytes.length - 64; i++) {
          if (bytes[i] === 0x04) {
            startIndex = i;
            break;
          }
        }

        if (startIndex !== -1 && startIndex + 65 <= bytes.length) {
          const rawKey = bytes.slice(startIndex, startIndex + 65);
          return rawKey;
        }

        // Fallback: try the common DER offset
        if (bytes.length >= 91) {
          // 26 (header) + 65 (key)
          const rawKey = bytes.slice(26);
          return rawKey;
        }
      }

      // If we can't determine the format, return the bytes as-is and let the browser handle it
      return bytes;
    } catch (error) {
      console.error('Error converting VAPID key:', error);
      throw new Error(
        'Invalid VAPID key format: ' + (error instanceof Error ? error.message : String(error))
      );
    }
  }*/
}

export default PushNotificationService;
